import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Flag to prevent multiple logout triggers
let isLoggingOut = false;

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle 401 if we're not already logging out
    if (error.response?.status === 401 && !isLoggingOut) {
      // Check if this is a login/register request - don't logout for auth failures
      const isAuthRequest = error.config?.url?.includes('/auth/login') || 
                           error.config?.url?.includes('/auth/register');
      
      if (!isAuthRequest) {
        // Verify that we actually have a token that should be cleared
        const token = localStorage.getItem('token');
        if (token) {
          isLoggingOut = true;
          console.warn('Session expired or invalid - logging out');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          // Use setTimeout to prevent multiple rapid redirects
          setTimeout(() => {
            isLoggingOut = false;
            window.location.href = '/login';
          }, 100);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  telegramLogin: (data) => api.post('/auth/telegram', data),
  googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
  getProfile: () => api.get('/auth/me'),
  refreshToken: () => api.post('/auth/refresh'),
  
  // WebAuthn / Biometric
  getWebAuthnChallenge: (email) => api.get('/auth/webauthn/challenge', { params: { email } }),
  registerWebAuthn: (data) => api.post('/auth/webauthn/register', data),
  loginWebAuthn: (data) => api.post('/auth/webauthn/login', data),
  checkWebAuthnRegistered: (email) => api.get(`/auth/webauthn/check/${email}`),
};

// User API
export const userAPI = {
  getBalance: () => api.get('/users/balance'),
  getDepositAddresses: () => api.get('/users/deposit-addresses'),
  getWallets: () => api.get('/users/wallets'),
  connectWallet: (data) => api.post('/users/wallets', data),
  disconnectWallet: (walletId) => api.delete(`/users/wallets/${walletId}`),
  getDeposits: () => api.get('/users/deposits'),
  createDeposit: (data) => api.post('/users/deposits', data),
  getWithdrawals: () => api.get('/users/withdrawals'),
  createWithdrawal: (data) => api.post('/users/withdrawals', data),
  
  // Profile
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.patch('/users/profile', data),
  
  // Security - Password
  changePassword: (data) => api.post('/users/change-password', data),
  
  // Security - 2FA
  get2FAStatus: () => api.get('/users/2fa/status'),
  setup2FA: () => api.post('/users/2fa/setup'),
  verify2FA: (data) => api.post('/users/2fa/verify', data),
  disable2FA: (data) => api.post('/users/2fa/disable', data),
  update2FASettings: (data) => api.post('/users/2fa/settings', data),
  
  // Security - Email 2FA
  sendEmail2FACode: (data) => api.post('/users/2fa/email/send-code', data || {}),
  verifyEmail2FA: (data) => api.post('/users/2fa/email/verify', data),
  resendEmail2FACode: () => api.post('/users/2fa/email/resend'),
  getEmail2FAStatus: () => api.get('/users/2fa/email/status'),
  
  // Security - Sessions
  getActiveSessions: () => api.get('/users/sessions'),
  terminateSession: (sessionId) => api.delete(`/users/sessions/${sessionId}`),
  terminateAllSessions: () => api.delete('/users/sessions'),
  
  // Security - IP Whitelist
  getIPWhitelist: () => api.get('/users/ip-whitelist'),
  addIPToWhitelist: (data) => api.post('/users/ip-whitelist', data),
  removeIPFromWhitelist: (data) => api.delete('/users/ip-whitelist', { data }),
  toggleIPWhitelist: (data) => api.patch('/users/ip-whitelist/toggle', data),
  
  // Notifications
  getNotifications: (isRead) => api.get('/users/notifications', { params: { is_read: isRead } }),
  markNotificationRead: (id) => api.patch(`/users/notifications/${id}/read`),
  markAllNotificationsRead: () => api.patch('/users/notifications/read-all'),
  getNotificationSettings: () => api.get('/users/notifications/settings'),
  updateNotificationSettings: (data) => api.patch('/users/notifications/settings', data),
  
  // Support
  sendSupportMessage: (data) => api.post('/users/support/message', data),
  
  // Support Tickets
  createTicket: (data) => api.post('/users/support/tickets', data),
  getMyTickets: () => api.get('/users/support/tickets'),
  replyToTicket: (ticketId, message) => api.post(`/users/support/tickets/${ticketId}/reply`, { message }),
  
  // Password Reset
  requestPasswordReset: (data) => api.post('/users/password-reset/request', data),
  confirmPasswordReset: (data) => api.post('/users/password-reset/confirm', data),
  validateResetToken: (token) => api.get(`/users/password-reset/validate/${token}`),
};

// Trading API
export const tradingAPI = {
  getSymbols: (limit = 1000) => api.get('/trading/symbols', { params: { limit } }),
  getPrice: (symbol) => api.get(`/trading/price/${symbol}`),
  getTicker: (symbol) => api.get(`/trading/ticker/${symbol}`),
  getOrderBook: (symbol, limit = 20) => api.get(`/trading/orderbook/${symbol}`, { params: { limit } }),
  getMarketTrades: (symbol, limit = 30) => api.get(`/trading/market-trades/${symbol}`, { params: { limit } }),
  openTrade: (data) => api.post('/trading/open', data),
  closeTrade: (tradeId) => api.post(`/trading/close/${tradeId}`),
  cancelOrder: (tradeId) => api.post(`/trading/cancel/${tradeId}`),
  getTrades: (status) => api.get('/trading/trades', { params: { status_filter: status } }),
  getTradeHistory: (tradeType, limit = 50, offset = 0) => api.get('/trading/history', { params: { trade_type: tradeType, limit, offset } }),
  getPendingOrders: () => api.get('/trading/pending-orders'),
  updateSlTp: (tradeId, data) => api.patch(`/trading/trades/${tradeId}/sltp`, data),
  // Spot trading
  spotBuy: (data) => api.post('/trading/spot/buy', data),
  spotSell: (data) => api.post('/trading/spot/sell', data),
  getHoldings: () => api.get('/trading/spot/holdings'),
  getSpotHistory: (limit = 50, offset = 0) => api.get('/trading/spot/history', { params: { limit, offset } }),
  getSpotPendingOrders: () => api.get('/trading/spot/pending-orders'),
  cancelSpotOrder: (orderId) => api.post(`/trading/spot/cancel-order/${orderId}`),
  // Portfolio
  getPortfolioSummary: () => api.get('/trading/portfolio/summary'),
  getDashboardBalances: () => api.get('/trading/dashboard/balances'),
  // Market Data (CoinGecko Pro)
  getTrending: () => api.get('/trading/market/trending'),
  getGainersLosers: (limit = 10) => api.get('/trading/market/gainers-losers', { params: { limit } }),
  getGlobalMarket: () => api.get('/trading/market/global'),
  getCoinDetails: (coinId) => api.get(`/trading/coin/${coinId}/details`),
  getCoinHistory: (coinId, days = 7) => api.get(`/trading/coin/${coinId}/history`, { params: { days } }),
  getMarketNews: () => api.get('/trading/market/news'),
};

// Notifications API
export const notificationsAPI = {
  getAll: (limit = 50, offset = 0) => api.get('/users/notifications', { params: { limit, offset } }),
  getUnreadOnly: (limit = 50) => api.get('/notifications', { params: { limit, unread_only: true } }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.patch(`/users/notifications/${notificationId}/read`),
  markAllAsRead: () => api.patch('/users/notifications/read-all'),
  delete: (notificationId) => api.delete(`/users/notifications/${notificationId}`),
  deleteAll: () => api.delete('/users/notifications'),
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard'),
  getExtendedDashboard: () => api.get('/admin/dashboard/extended'),
  getActivityGraph: (days = 30) => api.get('/admin/activity-graph', { params: { days } }),
  getManagerStats: () => api.get('/admin/manager-stats'),
  getAdminStats: () => api.get('/admin/admin-stats'),
  getClientDistributionStats: () => api.get('/admin/client-distribution-stats'),
  getPendingAlerts: () => api.get('/admin/pending-alerts'),
  
  // Team Management
  getTeam: () => api.get('/admin/team'),
  createTeamMember: (data) => api.post('/admin/team', data),
  updateTeamMember: (memberId, data) => api.patch(`/admin/team/${memberId}`, data),
  deleteTeamMember: (memberId) => api.delete(`/admin/team/${memberId}`),
  assignManager: (memberId, adminId) => api.patch(`/admin/team/${memberId}/assign?admin_id=${adminId}`),
  getManagers: () => api.get('/admin/managers'),
  getAdminsList: () => api.get('/admin/team/admins'),
  
  // Support Role Specific
  getPendingFirstDeposits: () => api.get('/admin/support/pending-first-deposits'),
  getNewClientsForSupport: () => api.get('/admin/support/new-clients'),
  getAdminsListForSupport: () => api.get('/admin/support/admins-list'),
  supportApproveFirstDeposit: (depositId, adminId, amount) => 
    api.post(`/admin/support/approve-first-deposit/${depositId}?admin_id=${adminId}`, null, { params: { amount } }),
  supportAssignClient: (clientId, adminId) =>
    api.post(`/admin/support/assign-client/${clientId}?admin_id=${adminId}`),
  
  // User Permissions
  getMyPermissions: () => api.get('/admin/my-permissions'),
  
  // Clients (CRM) with advanced filters
  getClients: (params) => api.get('/admin/clients', { params }),
  getClientDetails: (clientId) => api.get(`/admin/clients/${clientId}`),
  updateClient: (clientId, data) => api.patch(`/admin/clients/${clientId}`, null, { params: data }),
  fullUpdateClient: (clientId, data) => api.put(`/admin/clients/${clientId}`, data),
  
  // Balance Management
  adjustBalance: (clientId, data) => api.post(`/admin/clients/${clientId}/balance`, data),
  
  // Tags
  getAllTags: () => api.get('/admin/tags'),
  addClientTag: (clientId, tag) => api.post(`/admin/clients/${clientId}/tags?tag=${encodeURIComponent(tag)}`),
  removeClientTag: (clientId, tag) => api.delete(`/admin/clients/${clientId}/tags/${encodeURIComponent(tag)}`),
  
  // Predefined Tags Management
  getPredefinedTags: () => api.get('/admin/predefined-tags'),
  addPredefinedTag: (name, color, description) => api.post(`/admin/predefined-tags?name=${encodeURIComponent(name)}&color=${encodeURIComponent(color)}&description=${encodeURIComponent(description || '')}`),
  removePredefinedTag: (tagName) => api.delete(`/admin/predefined-tags/${encodeURIComponent(tagName)}`),
  updatePredefinedTag: (tagName, newName, color, description) => {
    const params = new URLSearchParams();
    if (newName) params.append('new_name', newName);
    if (color) params.append('color', color);
    if (description) params.append('description', description);
    return api.put(`/admin/predefined-tags/${encodeURIComponent(tagName)}?${params.toString()}`);
  },
  
  // Wallets & Blockchain
  getClientWallets: (clientId) => api.get(`/admin/clients/${clientId}/wallets`),
  addClientWallet: (clientId, data) => api.post(`/admin/clients/${clientId}/wallets`, data),
  getWalletBalance: (address, chain = 'ETH') => api.get(`/admin/wallets/${address}/balance`, { params: { chain } }),
  
  // Client Statuses
  getStatuses: () => api.get('/admin/statuses'),
  addStatus: (statusName) => api.post(`/admin/statuses?status_name=${encodeURIComponent(statusName)}`),
  removeStatus: (statusName) => api.delete(`/admin/statuses/${encodeURIComponent(statusName)}`),
  
  // Positions
  updatePosition: (positionId, data) => api.patch(`/admin/positions/${positionId}`, data),
  closePosition: (positionId, closeType, closePrice, closePnl) => api.post(`/admin/positions/${positionId}/close`, null, { params: { close_type: closeType, close_price: closePrice, close_pnl: closePnl } }),
  reopenPosition: (positionId) => api.post(`/admin/positions/${positionId}/reopen`),
  createPosition: (data) => api.post('/admin/positions/create', data),
  
  // Deposits & Withdrawals
  createDeposit: (data) => api.post('/admin/deposits/create', data),
  createWithdrawal: (data) => api.post('/admin/withdrawals/create', data),
  approveDeposit: (depositId, amount) => api.post(`/admin/deposits/${depositId}/approve`, null, { params: { amount } }),
  rejectDeposit: (depositId, reason) => api.post(`/admin/deposits/${depositId}/reject`, null, { params: { reason } }),
  deleteDeposit: (depositId) => api.delete(`/admin/deposits/${depositId}`),
  deleteWithdrawal: (withdrawalId) => api.delete(`/admin/withdrawals/${withdrawalId}`),
  processWithdrawal: (withdrawalId, action, txHash, reason) => api.post(`/admin/withdrawals/${withdrawalId}/process`, null, { params: { action, tx_hash: txHash, reason } }),
  resetDepositStatus: (depositId) => api.post(`/admin/deposits/${depositId}/reset`),
  resetWithdrawalStatus: (withdrawalId) => api.post(`/admin/withdrawals/${withdrawalId}/reset`),
  getAllRequests: (params) => api.get('/admin/requests', { params }),
  
  // Spot Balances
  getSpotBalances: (clientId) => api.get(`/admin/clients/${clientId}/spot-balances`),
  adjustSpotBalance: (clientId, data) => api.post(`/admin/clients/${clientId}/spot-balance`, data),
  
  // Mass Assignment
  massAssignClients: (clientIds, managerId, newStatus = null, newTag = null) => api.post('/admin/mass-assign', { 
    client_ids: clientIds, 
    assigned_to: managerId,
    new_status: newStatus,
    new_tag: newTag
  }),
  
  // Client History
  getClientHistory: (clientId, days = 90) => api.get(`/admin/clients/${clientId}/history`, { params: { days } }),
  
  // Mass Operations
  massBonusDistribution: (clientIds, amount, notes) => api.post('/admin/mass-bonus', { client_ids: clientIds, amount, notes }),
  massNotification: (clientIds, title, message, notificationType = 'system') => api.post('/admin/mass-notification', { client_ids: clientIds, title, message, notification_type: notificationType }),
  massStatusChange: (clientIds, newStatus) => api.post('/admin/mass-status-change', { client_ids: clientIds, new_status: newStatus }),
  massEmail: (clientIds, subject, body) => api.post('/admin/mass-email', { client_ids: clientIds, subject, body }),
  sendSingleEmail: (clientId, subject, body) => api.post('/admin/send-email', { client_id: clientId, subject, body }),
  
  // Admin Hierarchy
  assignAdminParent: (adminId, parentAdminId) => api.patch(`/admin/team/admin/${adminId}/assign-parent`, null, { params: { parent_admin_id: parentAdminId } }),
  removeAdminParent: (adminId) => api.patch(`/admin/team/admin/${adminId}/assign-parent`, null, { params: { parent_admin_id: null } }),
  getAdminHierarchy: (adminId) => api.get(`/admin/team/admin/${adminId}/hierarchy`),
  
  // Audit Log
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),
  getLoginHistory: (params) => api.get('/admin/login-history', { params }),
  
  // Support Tickets
  getTickets: (params) => api.get('/admin/tickets', { params }),
  replyToTicket: (ticketId, message) => api.post(`/admin/tickets/${ticketId}/reply?message=${encodeURIComponent(message)}`),
  updateTicketStatus: (ticketId, status) => api.patch(`/admin/tickets/${ticketId}/status?status=${status}`),
  
  // Users (Legacy)
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserById: (userId) => api.get(`/admin/users/${userId}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  
  // User Tags
  addUserTag: (userId, tag) => api.post(`/admin/users/${userId}/tags?tag=${encodeURIComponent(tag)}`),
  removeUserTag: (userId, tag) => api.delete(`/admin/users/${userId}/tags/${encodeURIComponent(tag)}`),
  getUserTags: (userId) => api.get(`/admin/users/${userId}/tags`),
  
  // Client IDO Management
  getClientIDO: (clientId) => api.get(`/admin/clients/${clientId}/ido`),
  createClientIDOTransaction: (clientId, data) => api.post(`/admin/clients/${clientId}/ido/transaction`, data),
  updateClientIDOTransaction: (clientId, txId, data) => api.patch(`/admin/clients/${clientId}/ido/transaction/${txId}`, data),
  deleteClientIDOTransaction: (clientId, txId) => api.delete(`/admin/clients/${clientId}/ido/transaction/${txId}`),
  updateClientIDOHolding: (clientId, asset, quantity) => api.patch(`/admin/clients/${clientId}/ido/holding/${asset}?quantity=${quantity}`),
  
  // Market Prices
  getMarketPrices: () => api.get('/admin/market/prices'),
  getMarketPrice: (symbol) => api.get(`/admin/market/price/${symbol}`),
  
  // Balance History
  getClientBalanceHistory: (clientId, days = 90) => api.get(`/admin/clients/${clientId}/balance-history`, { params: { days } }),
  
  // Trading Lock
  toggleTradingLock: (clientId, locked, reason = null) => api.post(`/admin/clients/${clientId}/trading-lock?locked=${locked}`, null, { params: { reason } }),
  
  // Send Notification to Client
  sendClientNotification: (clientId, title, message, notificationType = 'system') => 
    api.post(`/admin/clients/${clientId}/notification`, { title, message, notification_type: notificationType }),
  
  // Spot Trading (Admin)
  spotTrade: (clientId, data) => api.post(`/admin/clients/${clientId}/spot-trade`, data),
  updateSpotHolding: (clientId, data) => api.patch(`/admin/clients/${clientId}/spot-holding`, data),
  
  // Client Orders (Admin)
  getClientPendingOrders: (clientId) => api.get(`/admin/clients/${clientId}/pending-orders`),
  getClientSpotOrders: (clientId) => api.get(`/admin/clients/${clientId}/spot-orders`),
  cancelFuturesOrder: (clientId, orderId) => api.post(`/admin/clients/${clientId}/cancel-futures-order/${orderId}`),
  cancelSpotOrder: (clientId, orderId) => api.post(`/admin/clients/${clientId}/cancel-spot-order/${orderId}`),
  
  // Multi-chain Wallet Balance
  getWalletMultiChainBalance: (address) => api.get(`/admin/wallets/${address}/multi-chain-balance`),
  
  // Deposits
  getDeposits: (params) => api.get('/admin/deposits', { params }),
  
  // Withdrawals
  getWithdrawals: (params) => api.get('/admin/withdrawals', { params }),
  completeWithdrawal: (withdrawalId, txHash, notes) => api.patch(`/admin/withdrawals/${withdrawalId}/complete`, { tx_hash: txHash, notes }),
  
  // Trades
  getTrades: (params) => api.get('/admin/trades', { params }),
  updateTrade: (tradeId, data) => api.patch(`/admin/trades/${tradeId}`, data),
  deleteTrade: (tradeId) => api.delete(`/admin/trades/${tradeId}`),
  
  // CRM Notes
  addCRMNote: (userId, note, managerId) => api.post('/admin/crm-notes', { user_id: userId, note, manager_id: managerId }),
  getNotes: (userId) => api.get(`/admin/crm-notes/${userId}`),
  
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.patch('/admin/settings', data),
  getSystemSettings: () => api.get('/admin/settings'),
  updateSystemSettings: (data) => api.patch('/admin/settings', data),
  
  // Notifications
  getNotifications: (isRead) => api.get('/admin/notifications', { params: { is_read: isRead } }),
  markAsRead: (notificationId) => api.patch(`/admin/notifications/${notificationId}/read`),
  
  // IDO Management
  getIDOProjects: () => api.get('/ido/admin/projects'),
  getIDOProject: (projectId) => api.get(`/ido/admin/projects/${projectId}`),
  createIDOProject: (data) => api.post('/ido/admin/create', data),
  updateIDOProject: (projectId, data) => api.patch(`/ido/admin/projects/${projectId}`, data),
  getIDODeletePreview: (projectId) => api.get(`/ido/admin/projects/${projectId}/delete-preview`),
  deleteIDOProject: (projectId, deleteOptions) => api.delete(`/ido/admin/projects/${projectId}`, { data: deleteOptions }),
  
  // Email Settings
  getEmailSettings: () => api.get('/admin/email/settings'),
  updateEmailSettings: (data) => api.put('/admin/email/settings', data),
  sendTestEmail: (data) => api.post('/admin/email/test', data),
  
  // Email Templates
  getEmailTemplates: () => api.get('/admin/email/templates'),
  getEmailTemplate: (templateId) => api.get(`/admin/email/templates/${templateId}`),
  createEmailTemplate: (data) => api.post('/admin/email/templates', data),
  updateEmailTemplate: (templateId, data) => api.put(`/admin/email/templates/${templateId}`, data),
  deleteEmailTemplate: (templateId) => api.delete(`/admin/email/templates/${templateId}`),
  previewEmailTemplate: (templateId, variables) => api.post(`/admin/email/templates/${templateId}/preview`, variables),
  sendCustomEmail: (data) => api.post('/admin/email/send-custom', data),
};

// IDO API (for users)
export const idoAPI = {
  getActiveIDO: () => api.get('/ido/active'),
  getProjects: () => api.get('/ido/projects'),
  getProject: (projectId) => api.get(`/ido/projects/${projectId}`),
  buyTokens: (idoId, usdtAmount) => api.post('/ido/buy', { ido_id: idoId, usdt_amount: usdtAmount }),
  sellTokens: (idoId, tokenAmount) => api.post('/ido/sell', { ido_id: idoId, token_amount: tokenAmount }),
  getMyHoldings: () => api.get('/ido/my-holdings'),
  getMyHistory: () => api.get('/ido/my-history'),
};

export default api;
